/* eslint-disable jsx-a11y/no-onchange */
import type { ReactNode } from 'react';
import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import type { FieldProps } from 'formik';
import 'formik';
import { get, kebabCase } from 'lodash';
import { getInputClasses, getValidationStatus } from 'Components/frameworks/formik/inputsUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ImageValidationStatus } from 'Components/frameworks/image-validation-status/ImageValidationStatus';
import { ErrorNote } from './ErrorNote';
import { Label } from './Label';
type SelectOption = {
  isDisabled?: boolean;
  key?: string;
  name: string;
  value: string;
};

type SelectOptionGroup = {
  groupName: string;
  groupOptions: SelectOption[];
};

type Props = FieldProps & {
  customClasses?: string;
  dataTestId?: string;
  hideErrorMessage: boolean;
  hideImageValidation?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  label?: string;
  labelAdditionalInfo?: ReactNode;
  onBlur?: (arg0: React.FocusEvent<HTMLSelectElement>) => void;
  onChange?: (arg0: React.SyntheticEvent) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  onFocus?: (arg0: React.FocusEvent<HTMLSelectElement>) => void;
  onKeyDown?: (arg0: React.KeyboardEvent<HTMLSelectElement>) => void;
  onMouseDown?: (arg0: React.MouseEvent<HTMLSelectElement>) => void;
  options: Array<SelectOption> | Array<SelectOptionGroup>;
  placeholder?: string;
  withOptionGroup?: boolean;
};
export const Select = ({
  customClasses = '',
  dataTestId,
  field,
  form,
  hideErrorMessage,
  hideImageValidation,
  isDisabled = false,
  isRequired = false,
  label,
  labelAdditionalInfo,
  onBlur,
  onChange,
  onClick,
  onFocus,
  onKeyDown,
  onMouseDown,
  options,
  placeholder,
  withOptionGroup = false,
}: Props) => {
  if (!options) {
    throw new Error("Can't use select input type without options props");
  }

  // Select value defaults to empty string
  const selectedValue = get(form.values, field.name, '');
  const { isDefaultState, isErrored, isValidated } = getValidationStatus(
    { ...field, value: selectedValue },
    form,
    isDisabled,
  );
  const errorNoteId = `error-${kebabCase(field.name)}`;
  const onSelectChange = onChange ? onChange : field.onChange;
  const onSelectClick = onClick;
  // Formik's own blur handler keeps running so the field is still marked as touched.
  const onSelectBlur = (event: React.FocusEvent<HTMLSelectElement>) => {
    field.onBlur(event);
    onBlur?.(event);
  };
  const classes = getInputClasses({
    hasHeight: true,
    isDefaultState,
    isDisabled,
    isErrored,
    isValidated,
  });
  const selectStyle = customClasses ? customClasses : classes;
  return (
    <Fragment>
      {!!label && (
        <Label
          className="mx-15 mb-5"
          htmlFor={field.name}
          isErrored={isErrored}
          isRequired={isRequired}
          label={label}
          labelAdditionalInfo={labelAdditionalInfo}
        />
      )}
      <div className="relative isolate flex w-full items-center">
        <select
          aria-describedby={errorNoteId}
          autoComplete="off"
          className={classnames(
            'h-48',
            selectStyle,
            'pe-40',
            // Only the closed select takes the placeholder colour, not the options listed once open.
            placeholder && !selectedValue && !isDisabled && 'text-darkGrey [&_option]:text-black',
          )}
          data-testid={dataTestId}
          disabled={isDisabled}
          id={field.name}
          {...field}
          onBlur={onSelectBlur}
          onChange={onSelectChange}
          onClick={onSelectClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onMouseDown={onMouseDown}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}

          {withOptionGroup
            ? (options as SelectOptionGroup[]).map(({ groupName, groupOptions }) => {
                return (
                  groupOptions?.length && (
                    <optgroup label={groupName} key={groupName}>
                      {groupOptions.map(({ isDisabled: isDisabledOption, key, name, value }) => (
                        <option key={key || value} value={value} disabled={isDisabledOption}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  )
                );
              })
            : (options as SelectOption[]).map(
                ({ isDisabled: isDisabledOption, key, name, value }) => (
                  <option key={key || name + field.name} value={value} disabled={isDisabledOption}>
                    {name}
                  </option>
                ),
              )}
        </select>
        <div className="pointer-events-none absolute end-0 mx-20 flex items-center">
          <Icon name={ICONS.ARROW_DOWN} width=".625rem" />
        </div>
        {!isDisabled && !hideImageValidation && (
          <ImageValidationStatus
            className="absolute end-0 mx-40"
            isErrored={isErrored}
            isValidated={isValidated}
          />
        )}
      </div>
      {!hideErrorMessage && (
        <ErrorNote
          className="mx-15"
          isErrored={isErrored}
          errorMessage={get(form.errors, `${field.name}`) as string | undefined}
          errorNoteId={errorNoteId}
        />
      )}
    </Fragment>
  );
};
